import { saveProductData } from '@data/localStorageSetter';
import { useStoreProduct } from '@state/useStoreProduct';
import { useStoreItemsDetails } from '@state/useStoreItemsDetails';
import { useStoreToggleDrawer } from '@state/useStoreToggleDrawer';
import { useStoreTabData } from '@state/useStoreTabData';
import { useStoreProductCategory } from '@state/useStoreProductCategory';
import { useStoreVisibleItems } from '@state/useStoreVisibleItems';

/**
 * Fans the getDataAuthStore `product` tree out to every store the menu is drawn from.
 *
 * One place, because the menu is not one store: the same tree is reshaped into eight
 * (flat products, categories, item details, drawer and tab lists, …). The splash screen
 * calls this with what it downloaded; a sold-out refusal calls it with the same tree
 * patched (see flagUnavailableInMenu). A new store added to the menu belongs here, or a
 * refusal would leave it showing the product as orderable.
 *
 * Every setter replaces its data rather than appending, so applying a tree again is safe.
 * Order matches what the splash screen has always done.
 */
export function applyProductData(products) {
  saveProductData(products);
  useStoreProduct.getState().setFlatProducts(products);
  useStoreProduct.getState().updateProducts(products);
  useStoreItemsDetails.getState().setItemsDetail(products);
  useStoreToggleDrawer.getState().setCategoryList(products);
  useStoreTabData.getState().setFlatTabData(products);
  useStoreProductCategory.getState().setProdCategory(products);
  useStoreToggleDrawer.getState().setFlatCategoryList(products);
  refreshVisibleItems();
}

/**
 * The scroll layouts (Catalogue) copy each category's products into useStoreVisibleItems
 * once, on first mount, and never re-read them — so after a sold-out refusal a tile kept
 * its old "available" flag: drawn and tappable as orderable while its own price line
 * (read from item details) already said sold out. Re-copy every category already shown,
 * from the product categories just set above. Categories not shown yet fill in on mount.
 */
function refreshVisibleItems() {
  const { visibleItemsMap, setVisibleItems } = useStoreVisibleItems.getState();
  if (!visibleItemsMap.size) return;
  const { prodCategory } = useStoreProductCategory.getState();
  visibleItemsMap.forEach((_items, categoryUuid) => {
    const products = prodCategory.get(categoryUuid)?.products;
    if (products) setVisibleItems(categoryUuid, products);
  });
}
